import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { getAuthHeader } from '../../helpers/auth';
import { notify } from '../../helpers/utils';
import {
  User, Phone, Home, MapPin, Map, Globe,
  Flag, Building2, RotateCcw, CheckCircle, Loader, Info
} from 'lucide-react';

// ── Design tokens ─────────────────────────────────────────────────
const T = {
  bg:      'var(--nd-bg)',
  surface: 'var(--nd-surface)',
  surf2:   'var(--nd-surface-2)',
  surf3:   'var(--nd-surface-2)',
  border:  'var(--nd-w08)',
  bord2:   'var(--nd-w10)',
  text:    'var(--nd-text)',
  muted:   'var(--nd-tx35)',
  faint:   'var(--nd-w10)',
  blue:    '#3B82F6',
  teal:    '#22D3A5',
  amber:   '#FBBF24',
  red:     '#F87171',
  purple:  '#A78BFA',
  green:   '#34D399',
  font:    "'DM Sans', sans-serif",
  mono:    "'DM Mono', monospace",
};

const STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=DM+Sans:opsz,wght@9..40,400;9..40,500;9..40,600;9..40,700;9..40,800&family=DM+Mono:wght@400;500&display=swap');
  @keyframes spin    { to { transform: rotate(360deg); } }
  @keyframes fadeUp  { from{opacity:0;transform:translateY(8px)} to{opacity:1;transform:translateY(0)} }

  /* ── inputs ── */
  .ac-input, .ac-select, .ac-textarea {
    width: 100%; background: var(--nd-w05);
    border: 1px solid var(--nd-w10); border-radius: 9px;
    color: var(--nd-text); font-size: 13px; font-family: 'DM Sans', sans-serif;
    outline: none; transition: border-color 0.15s, background 0.15s;
  }
  .ac-input, .ac-select { padding: 10px 12px 10px 38px; height: 40px; }
  .ac-textarea           { padding: 10px 12px 10px 38px; resize: vertical; min-height: 80px; }
  .ac-input::placeholder,
  .ac-textarea::placeholder { color: var(--nd-tx22); }
  .ac-input:focus,
  .ac-select:focus,
  .ac-textarea:focus { border-color: rgba(59,130,246,0.55); background: rgba(59,130,246,0.05); }
  .ac-select { appearance: none; cursor: pointer; color-scheme: dark; }
  .ac-select option { background: var(--nd-surface-2); color: var(--nd-text); }

  /* ── form group label ── */
  .ac-label {
    display: block; font-size: 11px; font-weight: 700; letter-spacing: 0.06em;
    text-transform: uppercase; color: var(--nd-tx45);
    font-family: 'DM Sans', sans-serif; margin-bottom: 6px;
  }

  /* ── section card ── */
  .ac-section { animation: fadeUp 0.3s ease both; }

  /* ── action buttons ── */
  .ac-btn {
    display: inline-flex; align-items: center; gap: 7px;
    padding: 10px 22px; border-radius: 9px; font-size: 13px; font-weight: 700;
    font-family: 'DM Sans', sans-serif; cursor: pointer; transition: all 0.18s; border: none;
  }
  .ac-btn-reset {
    background: var(--nd-w05); color: var(--nd-tx45);
    border: 1px solid var(--nd-w10) !important;
  }
  .ac-btn-reset:hover:not(:disabled) { background: var(--nd-w08); color: var(--nd-tx70); }
  .ac-btn-submit {
    background: linear-gradient(135deg,#3B82F6,#22D3A5); color: var(--nd-text-strong);
    box-shadow: 0 4px 14px rgba(59,130,246,0.28);
  }
  .ac-btn-submit:hover:not(:disabled) { opacity: 0.88; transform: translateY(-1px); }
  .ac-btn-submit:disabled, .ac-btn-reset:disabled { opacity: 0.45; cursor: not-allowed; }

  /* ── hint text ── */
  .ac-hint { font-size: 10px; color: var(--nd-tx30); margin-top: 5px; display: flex; align-items: center; gap: 4px; }

  ::-webkit-scrollbar { width: 4px; }
  ::-webkit-scrollbar-track { background: transparent; }
  ::-webkit-scrollbar-thumb { background: var(--nd-w10); border-radius: 4px; }
`;

// ── Reusable section wrapper ──────────────────────────────────────
const Section = ({ Icon, iconColor, title, delay = 0, children }) => (
  <div className="ac-section" style={{
    background: T.surface, border: `1px solid ${T.border}`,
    borderRadius: 14, overflow: 'hidden', marginBottom: 16,
    boxShadow: '0 4px 16px rgba(0,0,0,0.22)',
    animationDelay: `${delay}s`,
  }}>
    {/* section head */}
    <div style={{ padding: '13px 18px', borderBottom: `1px solid ${T.border}`, display: 'flex', alignItems: 'center', gap: 9 }}>
      <div style={{
        width: 28, height: 28, borderRadius: 8, flexShrink: 0,
        background: `${iconColor}15`, border: `1px solid ${iconColor}30`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <Icon size={14} color={iconColor} />
      </div>
      <span style={{ fontSize: 13, fontWeight: 700, color: T.text }}>{title}</span>
    </div>
    <div style={{ padding: '20px 20px 6px' }}>{children}</div>
  </div>
);

// ── Form field with icon ──────────────────────────────────────────
const Field = ({ label, required, hint, icon: IconComp, iconColor = T.muted, children }) => (
  <div style={{ marginBottom: 18 }}>
    <label className="ac-label">
      {label}{required && <span style={{ color: T.red, marginLeft: 3 }}>*</span>}
    </label>
    <div style={{ position: 'relative' }}>
      {IconComp && (
        <IconComp
          size={13}
          color={iconColor}
          style={{ position: 'absolute', left: 11, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none', zIndex: 1 }}
        />
      )}
      {children}
    </div>
    {hint && (
      <div className="ac-hint">
        <Info size={9} />
        {hint}
      </div>
    )}
  </div>
);

// Textarea field has icon at top not middle
const TextareaField = ({ label, icon: IconComp, iconColor = T.muted, children }) => (
  <div style={{ marginBottom: 18 }}>
    <label className="ac-label">{label}</label>
    <div style={{ position: 'relative' }}>
      {IconComp && (
        <IconComp
          size={13}
          color={iconColor}
          style={{ position: 'absolute', left: 11, top: 12, pointerEvents: 'none', zIndex: 1 }}
        />
      )}
      {children}
    </div>
  </div>
);

// ── 2-col grid helper ─────────────────────────────────────────────
const Grid2 = ({ children }) => (
  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '0 20px' }}>
    {children}
  </div>
);

// ══════════════════════════════════════════════════════════════════
//   MAIN COMPONENT
// ══════════════════════════════════════════════════════════════════
const AddCustomer = () => {
  const config    = getAuthHeader();
  const navigate  = useNavigate();
  const [submitting, setSubmitting] = useState(false);

  const emptyForm = {
    client_name: '', phone_number: '', address: '',
    pin_location: '', client_city: '', client_state: '',
    client_country: '', date_added: '', added_by: '',
    priority: '', client_type: '',
  };
  const [formData, setFormData] = useState(emptyForm);

  const handleChange = (e) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const customerData = { ...formData, date_added: new Date() };
    try {
      setSubmitting(true);
      await axios.post(`${process.env.REACT_APP_API_BASE_URL}/customers/add`, customerData, config);
      notify('success', 'Customer added successfully!');
      navigate('/sales/search-customers');
    } catch (error) {
      if (error.response && error.response.status === 409) {
        notify('error', error.response.data.error);
      } else {
        notify('error', `Failed to add customer: ${error.response?.data?.message || error.message}`);
        console.error('Error adding customer:', error);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => setFormData(emptyForm);

  // ─────────────────────────────────────────────────────────────
  return (
    <div style={{ background: T.bg, fontFamily: T.font, color: T.text, padding: '14px 20px' }}>
      <style>{STYLES}</style>

      {/* ── PAGE HEADER ─────────────────────────────────────────── */}
      <div style={{
        background: T.surface, border: `1px solid ${T.border}`,
        borderRadius: 16, marginBottom: 18, overflow: 'hidden',
      }}>
        <div style={{ padding: '18px 24px' }}>
          <div style={{ fontSize: 9, color: T.muted, letterSpacing: '0.18em', marginBottom: 5 }}>
            NOVAA · SALES
          </div>
          <div style={{ fontSize: 26, fontWeight: 800, color: 'var(--nd-text-strong)', letterSpacing: '-0.03em', lineHeight: 1 }}>
            Add New Customer
          </div>
          <div style={{ fontSize: 12, color: T.muted, marginTop: 5 }}>
            Create a new customer profile
          </div>
        </div>
        <div style={{ height: 2, background: 'linear-gradient(90deg,#3B82F6,#22D3A5,transparent)' }} />
      </div>

      <form onSubmit={handleSubmit}>

        {/* ── PERSONAL INFORMATION ────────────────────────────── */}
        <Section Icon={User} iconColor={T.blue} title="Personal Information" delay={0}>
          <Grid2>
            <Field label="Customer Name" required icon={User} iconColor={T.blue}>
              <input
                className="ac-input"
                type="text"
                name="client_name"
                value={formData.client_name}
                onChange={handleChange}
                placeholder="Enter customer name"
                required
              />
            </Field>

            <Field label="Phone Number" required icon={Phone} iconColor={T.teal}>
              <input
                className="ac-input"
                type="text"
                name="phone_number"
                value={formData.phone_number}
                onChange={handleChange}
                placeholder="+92-XXX-XXXXXXX"
                required
              />
            </Field>
          </Grid2>

          <TextareaField label="Address" icon={Home} iconColor={T.purple}>
            <textarea
              className="ac-textarea"
              name="address"
              value={formData.address}
              onChange={handleChange}
              placeholder="Enter full address"
              rows={3}
            />
          </TextareaField>
        </Section>

        {/* ── LOCATION DETAILS ────────────────────────────────── */}
        <Section Icon={MapPin} iconColor={T.teal} title="Location Details" delay={0.06}>
          <Grid2>
            <Field label="Pin Location" icon={MapPin} iconColor={T.red}>
              <input
                className="ac-input"
                type="text"
                name="pin_location"
                value={formData.pin_location}
                onChange={handleChange}
                placeholder="GPS coordinates or pin URL"
              />
            </Field>

            <Field label="City" icon={Building2} iconColor={T.blue}>
              <select
                className="ac-select"
                name="client_city"
                value={formData.client_city}
                onChange={handleChange}
              >
                <option value="">Select City</option>
                <option value="Karachi">Karachi</option>
                <option value="Lahore">Lahore</option>
                <option value="Islamabad">Islamabad</option>
                <option value="Quetta">Quetta</option>
                <option value="Peshawar">Peshawar</option>
                <option value="Multan">Multan</option>
                <option value="Faisalabad">Faisalabad</option>
              </select>
            </Field>

            <Field label="State / Province" icon={Map} iconColor={T.amber}>
              <select
                className="ac-select"
                name="client_state"
                value={formData.client_state}
                onChange={handleChange}
              >
                <option value="">Select State</option>
                <option value="Sindh">Sindh</option>
                <option value="Punjab">Punjab</option>
                <option value="Balochistan">Balochistan</option>
                <option value="Khyber Pakhtunkhwa">Khyber Pakhtunkhwa</option>
                <option value="Gilgit-Baltistan">Gilgit-Baltistan</option>
              </select>
            </Field>

            <Field label="Country" icon={Globe} iconColor={T.teal}>
              <select
                className="ac-select"
                name="client_country"
                value={formData.client_country}
                onChange={handleChange}
              >
                <option value="">Select Country</option>
                <option value="Pakistan">Pakistan</option>
                <option value="USA">USA</option>
              </select>
            </Field>
          </Grid2>
        </Section>

        {/* ── CUSTOMER CLASSIFICATION ─────────────────────────── */}
        <Section Icon={Flag} iconColor={T.purple} title="Customer Classification" delay={0.12}>
          <Grid2>
            <Field
              label="Priority Level"
              icon={Flag}
              iconColor={T.amber}
              hint="Set customer priority for service scheduling"
            >
              <select
                className="ac-select"
                name="priority"
                value={formData.priority}
                onChange={handleChange}
              >
                <option value="">Select Priority</option>
                <option value="1">🔴 High Priority</option>
                <option value="2">🟡 Medium Priority</option>
                <option value="3">🟢 Low Priority</option>
              </select>
            </Field>

            <Field
              label="Customer Type"
              icon={Building2}
              iconColor={T.purple}
              hint="Classification helps in service customization"
            >
              <select
                className="ac-select"
                name="client_type"
                value={formData.client_type}
                onChange={handleChange}
              >
                <option value="">Select Type</option>
                <option value="1">🏠 Residential</option>
                <option value="2">🏢 Commercial</option>
                <option value="3">🏭 Industrial</option>
              </select>
            </Field>
          </Grid2>
        </Section>

        {/* ── ACTION BAR ──────────────────────────────────────── */}
        <div style={{
          background: T.surface, border: `1px solid ${T.border}`,
          borderRadius: 14, padding: '16px 20px',
          display: 'flex', justifyContent: 'flex-end', gap: 10,
          boxShadow: '0 4px 16px rgba(0,0,0,0.22)',
        }}>
          <button
            type="button"
            className="ac-btn ac-btn-reset"
            onClick={resetForm}
            disabled={submitting}
          >
            <RotateCcw size={13} />
            Reset Form
          </button>

          <button
            type="submit"
            className="ac-btn ac-btn-submit"
            disabled={submitting}
          >
            {submitting ? (
              <>
                <Loader size={13} style={{ animation: 'spin 0.8s linear infinite' }} />
                Adding Customer…
              </>
            ) : (
              <>
                <CheckCircle size={13} />
                Add Customer
              </>
            )}
          </button>
        </div>

      </form>
    </div>
  );
};

export default AddCustomer;